import React from "react"
import {
  roundCurrency,
  toCents,
  fromCents,
  isNearZero,
  strictRound2,
} from "../utils/costsUtils"
import PropTypes from "prop-types"
import ExpensesInput from "./ExpensesInput"

import { Box, Button, Grid, Paper, Typography } from "@mui/material"

import { useEffect, useState } from "react"

const ActionsColumn = ({
  formData,
  allocatedAmount = 0,
  remainingAmount = 0,
  setAllocation,
  selectedItems = {},
  projectsData = [],
  projectsHeaders = [],
  expandedAccordions = {},
  setExpandedAccordions = () => {},
  allocation = {},
  setOrganizationAmount = () => {},
}) => {
  const handleReset = () => {
    if (typeof setAllocation === "function") {
      const singleProjectMode =
        Array.isArray(projectsHeaders) && projectsHeaders.length === 1
      const mainProjectId = singleProjectMode
        ? projectsHeaders[0].projectId
        : null

      setAllocation(prev => {
        if (!singleProjectMode) {
          const resetAll = {}
          for (const key in prev) {
            if (!Object.prototype.hasOwnProperty.call(prev, key)) continue
            resetAll[key] = { ...prev[key] }
            if (typeof resetAll[key].allocated === "number")
              resetAll[key].allocated = 0
            if (Array.isArray(resetAll[key].decomposition))
              resetAll[key].decomposition = resetAll[key].decomposition.map(
                () => 0,
              )
          }
          resetAll.organization = {
            ...(resetAll.organization || {}),
            allocated: 0,
          }
          return resetAll
        }

        const partidaToProject = new Map()
        projectsData.forEach(project => {
          if (!Array.isArray(project.chapters)) return
          const stack = [...project.chapters]
          while (stack.length) {
            const ch = stack.pop()
            if (Array.isArray(ch.partidas)) {
              ch.partidas.forEach(p => {
                if (p && p.partidaId)
                  partidaToProject.set(
                    p.partidaId,
                    p.projectId || project.projectId,
                  )
              })
            }
            if (Array.isArray(ch.chapters)) stack.push(...ch.chapters)
          }
        })

        const next = { ...prev }
        for (const key in next) {
          if (!Object.prototype.hasOwnProperty.call(next, key)) continue
          if (key === "organization") continue // organización se resetea después aparte
          const entry = next[key]
          if (!entry || typeof entry !== "object") continue
          const entryProjectId = entry.projectId || partidaToProject.get(key)
          if (entryProjectId && entryProjectId === mainProjectId) {
            next[key] = {
              ...entry,
              allocated: 0,
              decomposition: Array.isArray(entry.decomposition)
                ? entry.decomposition.map(() => 0)
                : entry.decomposition,
            }
          }
        }

        // Mantener organización sin cambios en modo single project
        // (no se reinicia su monto)
        // next.organization permanece igual
        return next
      })
    }
    // No tocar organizationAmount en modo single project
  }
  const total = Number(formData?.total) || 0
  const [remaining, setRemaining] = useState(() => {
    const rem = strictRound2(total - allocatedAmount)
    return isNearZero(rem) ? 0 : rem
  })

  useEffect(() => {
    let rem = strictRound2(total - allocatedAmount)
    if (isNearZero(rem)) rem = 0
    setRemaining(rem)
  }, [total, allocatedAmount])

  const isDistributeDisabled = remaining <= 0
  const distributeValueIntoParts = (totalValue, n) => {
    if (n <= 0) return []
    const totalCents = toCents(totalValue)
    const base = Math.floor(totalCents / n)
    const remainder = totalCents % n
    return Array.from({ length: n }, (_, i) =>
      fromCents(base + (i < remainder ? 1 : 0)),
    )
  }

  const collectSelectedPartidasFromProjects = () => {
    const partidas = []
    projectsData.forEach(project => {
      if (!Array.isArray(project.chapters)) return
      const stack = [...project.chapters]
      while (stack.length) {
        const ch = stack.shift()
        if (Array.isArray(ch.partidas)) {
          ch.partidas.forEach(p => {
            const partidaSelected = !!selectedItems[p.partidaId]
            const decompSelected =
              Array.isArray(p.decompositions) &&
              p.decompositions.some(
                (_, i) => !!selectedItems[`${p.partidaId}_${i}`],
              )
            if (partidaSelected || decompSelected) partidas.push(p)
          })
        }
        if (Array.isArray(ch.chapters)) stack.push(...ch.chapters)
      }
    })
    return partidas
  }

  const handleDistribute = () => {
    const toDistribute = remainingAmount
    const singleProjectMode =
      Array.isArray(projectsHeaders) && projectsHeaders.length === 1
    const mainProjectId = singleProjectMode
      ? projectsHeaders[0].projectId
      : null
    const ts = new Date().toISOString()
    if (toDistribute <= 0) {
      console.warn(
        "[Costes][Distribuir]",
        ts,
        "Abortado: monto a distribuir <= 0",
        { remaining: toDistribute },
      )
      return
    }
    let partidas = collectSelectedPartidasFromProjects()

    if (singleProjectMode) {
      const partidaToProject = new Map()
      projectsData.forEach(project => {
        if (!Array.isArray(project.chapters)) return
        const stack = [...project.chapters]
        while (stack.length) {
          const ch = stack.pop()
          if (Array.isArray(ch.partidas)) {
            ch.partidas.forEach(p => {
              if (p && p.partidaId)
                partidaToProject.set(
                  p.partidaId,
                  p.projectId || project.projectId,
                )
            })
          }
          if (Array.isArray(ch.chapters)) stack.push(...ch.chapters)
        }
      })
      partidas = partidas.filter(p => {
        const pid = p.projectId || partidaToProject.get(p.partidaId)
        return pid === mainProjectId
      })
    }
    if (partidas.length === 0) {
      console.warn(
        "[Costes][Distribuir]",
        ts,
        "Abortado: no hay partidas seleccionadas",
      )
      return
    }

    const parts = distributeValueIntoParts(toDistribute, partidas.length)

    setAllocation(prev => {
      const next = { ...prev }
      let distributedSumCents = 0
      console.groupCollapsed(`[Costes][Distribuir] ${ts}`)
      console.log("Monto restante antes de distribuir:", remaining)
      console.log("Total a distribuir:", toDistribute)
      partidas.forEach((p, idx) => {
        const partVal = parts[idx] || 0
        const partValCents = toCents(partVal)
        const decompLength = Array.isArray(p.decompositions)
          ? p.decompositions.length
          : 0
        const partidaExpanded = !!(
          expandedAccordions && expandedAccordions[p.partidaId]
        )
        const existing = next[p.partidaId] || {}

        if (decompLength > 0 && partidaExpanded) {
          const selectedDecompIndices = p.decompositions
            .map((d, i) => i)
            .filter(i => !!selectedItems[`${p.partidaId}_${i}`])
          if (selectedDecompIndices.length > 0) {
            const decompParts = distributeValueIntoParts(
              partVal,
              selectedDecompIndices.length,
            )
            const newDecomp = Array.from({ length: decompLength }, (_, i) => {
              const selIdx = selectedDecompIndices.indexOf(i)
              const existingVal = existing.decomposition?.[i] ?? 0
              return selIdx >= 0
                ? strictRound2(existingVal + decompParts[selIdx])
                : strictRound2(existingVal)
            })
            const prevDecompTotal = existing.decomposition
              ? existing.decomposition.reduce((a, b) => a + (b || 0), 0)
              : 0
            const newDecompTotal = newDecomp.reduce((a, b) => a + (b || 0), 0)
            const delta = strictRound2(newDecompTotal - prevDecompTotal)
            const deltaCents = toCents(delta)
            distributedSumCents += deltaCents
            const newAllocated = strictRound2(
              (existing.allocated || 0) + fromCents(deltaCents),
            )
            next[p.partidaId] = {
              ...existing,
              projectId: p.projectId || existing.projectId,
              allocated: newAllocated,
              decomposition: newDecomp,
            }
            console.log("Partida (descomposición)", p.partidaId, {
              partValAsignado: partVal,
              delta,
              newDecomp,
              selectedDecompIndices,
            })
            return
          }
        }

        const existingAllocated = strictRound2(existing.allocated || 0)
        const newAllocated = strictRound2(
          fromCents(toCents(existingAllocated) + partValCents),
        )
        next[p.partidaId] = {
          ...existing,
          projectId: p.projectId || existing.projectId,
          allocated: newAllocated,
          decomposition: Array.from({ length: decompLength }, () => 0),
        }
        distributedSumCents += partValCents
        console.log("Partida", p.partidaId, { partVal: newAllocated })
      })
      console.log(
        "Suma distribuida en esta operación:",
        fromCents(distributedSumCents),
      )
      const totalAllocatedAfter = Object.values(next).reduce(
        (acc, val) =>
          acc + (typeof val === "object" && val.allocated ? val.allocated : 0),
        0,
      )
      console.log(
        "Total allocated después de distribuir (estimado):",
        strictRound2(totalAllocatedAfter),
      )
      console.groupEnd()
      return next
    })
  }

  let assignedOtherProjects = 0
  let assignedThisProject = 0
  if (
    Array.isArray(projectsHeaders) &&
    projectsHeaders.length === 1 &&
    allocation
  ) {
    const mainProjectId = projectsHeaders[0].projectId

    const partidaToProject = new Map()
    const buildMap = () => {
      projectsData.forEach(project => {
        if (!Array.isArray(project.chapters)) return
        const stack = [...project.chapters]
        while (stack.length) {
          const ch = stack.pop()
          if (Array.isArray(ch.partidas)) {
            ch.partidas.forEach(p => {
              if (p && p.partidaId)
                partidaToProject.set(
                  p.partidaId,
                  p.projectId || project.projectId,
                )
            })
          }
          if (Array.isArray(ch.chapters)) stack.push(...ch.chapters)
        }
      })
    }
    buildMap()

    assignedOtherProjects = Object.entries(allocation).reduce(
      (acc, [pid, entry]) => {
        if (!entry || typeof entry !== "object") return acc
        if (pid === "organization") return acc // clave especial
        const val = typeof entry.allocated === "number" ? entry.allocated : 0
        if (!val) return acc
        const entryProjectId = entry.projectId || partidaToProject.get(pid)
        if (entryProjectId && entryProjectId !== mainProjectId) return acc + val
        return acc
      },
      0,
    )
    assignedThisProject = Object.entries(allocation).reduce(
      (acc, [pid, entry]) => {
        if (!entry || typeof entry !== "object") return acc
        if (pid === "organization") return acc
        const val = typeof entry.allocated === "number" ? entry.allocated : 0
        if (!val) return acc
        const entryProjectId = entry.projectId || partidaToProject.get(pid)
        if (entryProjectId === mainProjectId) return acc + val
        return acc
      },
      0,
    )
    if (assignedOtherProjects && assignedOtherProjects < 0.005)
      assignedOtherProjects = 0
    if (assignedThisProject && assignedThisProject < 0.005)
      assignedThisProject = 0
  }

  return (
    <Grid item xs={12} md={3}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 1,
          mt: 2,
        }}
      >
        <Typography
          sx={{
            fontWeight: "bold", // Cambiado a bold para negrita
            flexShrink: 0,
            mt: 2,
          }}
        >
          Acciones
        </Typography>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          height: "463px",
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ mb: 2 }}>
          <ExpensesInput
            value={Math.abs(remainingAmount) < 0.005 ? 0 : remainingAmount}
            onChange={() => {}}
            label="Monto restante"
            size="medium"
            readOnly
            allowNegative={true}
            sx={{ mb: 2 }}
            fullWidth
          />
          <ExpensesInput
            value={formData.subtotal}
            onChange={() => {}}
            label="Monto total"
            readOnly
            size="medium"
            fullWidth
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#3a3a3a",
              color: "white",
              "&:hover": { backgroundColor: "#222" },
            }}
            fullWidth
            disabled={isDistributeDisabled}
            onClick={handleDistribute}
          >
            Distribuir gasto
          </Button>
          <Button
            variant="outlined"
            sx={{
              borderColor: "#3a3a3a",
              color: "#3a3a3a",
              "&:hover": { borderColor: "#222", color: "#222" },
            }}
            fullWidth
            onClick={handleReset}
          >
            Restablecer
          </Button>
        </Box>

        {Array.isArray(projectsHeaders) && projectsHeaders.length === 1 && (
          <Box
            sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 0.25 }}
          >
            <Typography variant="caption" sx={{ fontWeight: "bold" }}>
              Imputado:
            </Typography>
            <Typography variant="caption" color="text.secondary">
              En este proyecto:{" "}
              {assignedThisProject.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              En otros proyectos:{" "}
              {assignedOtherProjects.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
          </Box>
        )}
      </Paper>
    </Grid>
  )
}

ActionsColumn.propTypes = {
  formData: PropTypes.shape({
    total: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    subtotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  allocatedAmount: PropTypes.number,
  remainingAmount: PropTypes.number,
  setAllocation: PropTypes.func,
  selectedItems: PropTypes.object,
  projectsData: PropTypes.array,
  projectsHeaders: PropTypes.array,
  expandedAccordions: PropTypes.object,
  setExpandedAccordions: PropTypes.func,
  allocation: PropTypes.object,
  setOrganizationAmount: PropTypes.func,
}

export default ActionsColumn
